<template>
  <div id="app" :class="{ mobileDevice: mobile, backgroundClear: backgroundClear, hideHeader: brandQuery && brandQuery.hideHeader === 'true' }">
    <main>
      <transition :name="transitionName">
        <router-view />
      </transition>
    </main>

    <!-- MsgPopup -->
    <DialogPopup></DialogPopup>
  </div>
</template>

<script>
import webSocket from '@/util/websocket';
import DialogPopup from '@/components/DialogPopup';
import helpers from '@/util/helpers';
import { mapGetters } from 'vuex';

export default {
  name: 'App',
  metaInfo() {
    return {
      title: `클라우드기반의 채팅상담 솔루션`,
      titleTemplate: `${this.$isSeller ? '셀러게이트' : '클라우드게이트'} - %s`
    };
  },
  components: {
    DialogPopup
  },
  computed: {
    ...mapGetters(['chatRoomData'])
  },
  data() {
    return {
      brandQuery: window.sessionStorage.getItem('BRAND_QUERY') ? JSON.parse(window.sessionStorage.getItem('BRAND_QUERY')) : undefined,
      senderUid: window.localStorage.getItem(process.env.VUE_APP_LOGIN_ID)
        ? window.localStorage.getItem(process.env.VUE_APP_LOGIN_ID)
        : window.localStorage.getItem(process.env.VUE_APP_UUID_KEY),
      transitionName: '',
      mobile: helpers.isMobile(),
      backgroundClear: false
    };
  },
  watch: {
    $route(to, from) {
      this.brandQuery = window.sessionStorage.getItem('BRAND_QUERY') ? JSON.parse(window.sessionStorage.getItem('BRAND_QUERY')) : undefined;
      if (!from.meta.pageIndex) {
        this.transitionName = '';
      } else {
        if (to.meta.pageIndex && from.meta.pageIndex) {
          this.transitionName = to.meta.pageIndex > from.meta.pageIndex ? 'next' : 'prev';
        } else {
          this.transitionName = 'next';
        }
      }
    }
  },
  created() {
    const path = window.location.pathname;
    this.backgroundClear = path === '/naver/chat/open' || path === '/kakao/chat/open';
    let style = document.createElement('link');
    style.type = 'text/css';
    style.rel = 'stylesheet';
    style.href = `//public-common-sdk.s3.ap-northeast-2.amazonaws.com/fonts/twc.css?timestemp=${new Date().getTime()}`;
    document.head.appendChild(style);
  },
  mounted() {
    // 소켓 연결
    webSocket.connect();

    // 외부에서 강제 종료 요청 이벤트
    window.addEventListener('message', (e) => {
      if (e.data.chatMsg) {
        if (e.data.chatMsg === 'conclude') {
          this.$http({
            method: 'POST',
            url: '/api/external/webchat/closeChatRoom',
            data: {
              chatRoomId: this.chatRoomData.chatRoomId
            }
          }).then(() => {
            this.$route.matched[0].instances.default.$refs.header.closeFrame();
          });
        } else {
          try {
            if (this.$isIframe) {
              window.parent.postMessage({ messageType: 'HIDDEN_CHATROOM', data: e.data.chatMsg }, '*');
            } else if (window.opener) {
              window.opener.postMessage({ messageType: 'HIDDEN_CHATROOM', data: e.data.chatMsg }, '*');
            }
          } catch (err) {
            console.log('postMessage HIDDEN_CHATROOM failed', err);
          }
        }
      }
    });

    // webSocket Start
    window.$mitt.on('reconnect-start', () => {
      setTimeout(() => {
        webSocket.connect();
      }, 3000);
    });

    // 웹소켓 연결
    window.$mitt.on('socket-connection-success', () => {
      this.senderUid = window.localStorage.getItem(process.env.VUE_APP_LOGIN_ID)
        ? window.localStorage.getItem(process.env.VUE_APP_LOGIN_ID)
        : window.localStorage.getItem(process.env.VUE_APP_UUID_KEY);
      this.webChatSubscribe();
    });

    // 윈도우 종료시
    window.addEventListener('beforeunload', () => {
      if (!this.chatRoomData.isEnd && this.chatRoomData.chatRoomId) {
        this.$http({
          method: 'POST',
          url: `/api/external/webchat/close/${this.chatRoomData.chatRoomId}`,
          async: false
        });
      }
    });

    this.$mitt.on('windowOpen', (data) => {
      if (this.brandQuery && this.brandQuery.appId && navigator.userAgent.indexOf(this.brandQuery.appId) > -1) {
        const parameters = {
          func: 'onOrderPage',
          url: data.url
        };
        if (navigator.userAgent.indexOf('Android') > -1) {
          window.AppJSInterface.onOrderPage(JSON.stringify(parameters));
        } else {
          window.webkit.messageHandlers.AppJSInterface.postMessage(parameters);
        }
      } else {
        window.open(data.url, data.target, data.features);
      }
    });
  },
  methods: {
    // 구독
    webChatSubscribe() {
      this.$http({
        method: 'POST',
        url: '/api/external/webchat/subscribeUrl',
        params: {
          senderUid: this.senderUid
        }
      }).then((res) => {
        if (res.status !== 200 || res.data.code !== 0) {
          let message = res.data ? res.data.message : res.statusText;
          this.$mitt.emit('DialogPopup', {
            useDialog: true,
            message: '<dl class="txt"><dd>' + message + '</dd></dl>'
          });
          return false;
        }
        let data = res.data.data;
        if (this.$route.matched.length > 0 && this.$route.matched[0].path === '/webChat') {
          this.$route.matched[0].instances.default.visibilitychange();
        }
        webSocket.subscribe({
          key: 'webchat-main-subscribeUrl-' + this.senderUid,
          subscribeUrl: data.subscribeUrl,
          callback: (body) => {
            window.$mitt.emit('subscribeBody', body);
            // SDK 배지 컨트롤을 위한 이벤트 별도 관리용, 대화방용은 'subscribeBody'에서 처리
            switch (body.changeType) {
              case 'NEW_CHAT_MESSAGE':
                if (body.data.chatRoomId === this.chatRoomData.chatRoomId) {
                  if (body.data.lastMessageInfo.messageType === 'START') {
                    window.sessionStorage.setItem(
                      'CURRENTLY_CHATROOM',
                      JSON.stringify({
                        id: body.data.chatRoomId,
                        status: 'ACTIVE'
                      })
                    );

                    // 안전한 postMessage 사용: parent/opener 존재 및 닫힘 여부 확인, 가능한 경우 명시적 origin 사용
                    try {
                      if (this.$isIframe) {
                        window.parent.postMessage({ messageType: 'STATUS', data: 'ACTIVE' }, '*');
                      } else if (window.opener) {
                        window.opener.postMessage({ messageType: 'STATUS', data: 'ACTIVE' }, '*');
                      }
                    } catch (e) {
                      // 안전하게 실패 무시
                      console.log('postMessage STATUS failed', e);
                    }
                  }

                  if (
                    body.data.lastMessageInfo.messageType === 'START' ||
                    (body.data.lastMessageInfo.senderType === 'AGENT' && body.data.lastMessageInfo.messageType !== 'END') ||
                    (body.data.lastMessageInfo.senderType === 'SYSTEM' && body.data.lastMessageInfo.messageType === 'AUTO_CLOSE')
                  ) {
                    let content = JSON.parse(body.data.lastMessageInfo.content);
                    let messageData = {
                      senderType: body.data.lastMessageInfo.senderType,
                      senderName: body.data.lastMessageInfo.senderName,
                      createDate: body.data.lastMessageInfo.createDate,
                      content: {
                        text: content.text || content.url
                      }
                    };

                    if (this.brandQuery.appId && navigator.userAgent.indexOf(this.brandQuery.appId) > -1) {
                      // 모바일 화면에서 'newChatMessage' 이벤트를 SDK로 전달
                      const parameters = {
                        func: 'newChatMessage',
                        data: messageData
                      };
                      if (navigator.userAgent.indexOf('Android') > -1) {
                        window.AppJSInterface.newChatMessage(JSON.stringify(parameters));
                      } else {
                        window.webkit.messageHandlers.AppJSInterface.postMessage(parameters);
                      }
                    } else {
                      // PC 웹 화면에서 'newChatMessage' 이벤트를 부모 창 또는 오프너 창으로 전달
                      try {
                        if (this.$isIframe) {
                          window.parent.postMessage({ messageType: 'NEW_CHAT_MESSAGE', data: messageData }, '*');
                        } else if (window.opener) {
                          window.opener.postMessage({ messageType: 'NEW_CHAT_MESSAGE', data: messageData }, '*');
                        }
                      } catch (e) {
                        console.log('postMessage NEW_CHAT_MESSAGE failed', e);
                      }
                    }
                  }
                }
                break;
              case 'CLOSE_CHAT_ROOM':
                if (body.data.chatRoomId === this.chatRoomData.chatRoomId) {
                  window.sessionStorage.setItem(
                    'CURRENTLY_CHATROOM',
                    JSON.stringify({
                      id: body.data.chatRoomId,
                      status: body.data.status
                    })
                  );
                  try {
                    if (this.$isIframe) {
                      window.parent.postMessage({ messageType: 'CLOSE_CHAT_ROOM', data: true }, '*');
                    } else if (window.opener) {
                      window.opener.postMessage({ messageType: 'CLOSE_CHAT_ROOM', data: true }, '*');
                    }
                  } catch (e) {
                    console.log('postMessage CLOSE_CHAT_ROOM failed', e);
                  }
                }
                break;
            }
          }
        });
      });
    }
  }
};
</script>

<style lang="scss">
@import '@/assets/scss/_common.scss';
</style>
